import React, { useState, useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ViewProduct } from '../../models/ViewProduct.model';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { SubContext } from '../../enums/SubContext.enum';
import { PageContext } from '../../enums/PageContext.enum';
import { productTitlePart1, productTitlePart2, scrollToRef } from '../../util';
import { SelectionType } from '../../enums/SelectionType.enum';
import { assetPathFnState, pageContextState, abTestState } from '../../recoil/atoms';
import { isMobileState, productsState } from '../../recoil/selectors';
import { LazyImageLoader } from './LazyImageLoader';
import styles from './ProductRelatedItems.module.scss';
import classnames from 'classnames';
import { ProductCategory } from '../../enums/ProductCategory.enum';
import { useProductSelection } from '../../hooks/useProductSelection';

interface Props {
  product?: ViewProduct;
  additionalClassName?: string;
  productSectionRef?: React.RefObject<HTMLDivElement>;
}

export const ProductRelatedItems: React.FC<Props> = ({ product, additionalClassName, productSectionRef }) => {
  const products = useRecoilValue(productsState);
  const isMobile = useRecoilValue(isMobileState);
  const assetPathFn = useRecoilValue(assetPathFnState);
  const pageContext = useRecoilValue(pageContextState);
  const abTests = useRecoilValue(abTestState);
  const selectionType = SelectionType.ProductSection;
  const { setHandle, setSubContext } = useProductSelection(selectionType, '0');

  const [relatedItems, setRelatedItems] = useState<RelatedItem[]>([]);

  const selectStyleAndScroll = (handle: ProductHandle, subContext: SubContext) => {
    if (pageContext === PageContext.GiftCardPage || !productSectionRef) {
      window.location.href = `/products/${handle}`;
    } else {
      setHandle(handle);
      setSubContext(subContext);
      productSectionRef && scrollToRef(productSectionRef);
    }
  };

  useEffect(() => {
    setRelatedItems(product?.handle ? dynamicRelatedItems(product.handle, Object.values(products)) : mostPopularItems(Object.values(products)));
  }, [product?.handle, products]);

  return (
    <div className={classnames(styles.relatedProductsSection, additionalClassName ? styles[additionalClassName] : '')}>
      <div className={styles.relatedProductTitle}>
        <h2>{abTests['AB2021-19'] ? 'Customers also bought' : abTests['AB2021-20'] ? "Honeylove's Most Popular" : 'Our picks for you'}</h2>
      </div>
      {isMobile && (
        <div>
          <div className={styles.row}>{relatedItems.map(renderRelatedItemMobile(assetPathFn, selectStyleAndScroll))}</div>
        </div>
      )}
      {!isMobile && <div className={styles.relatedProductWrapper}>{relatedItems.map(renderRelatedItemDesktop(assetPathFn, selectStyleAndScroll))}</div>}
    </div>
  );
};

const items = (product: ViewProduct) => {
  return {
    name1: productTitlePart1(product.title),
    name2: productTitlePart2(product.title),
    handle: product.handle,
    subtitle: product.description,
    image: relatedProductImage(product.handle),
    popularity: getPopularity(product.handle),
  };
};

const relatedProductImage = (handle: ProductHandle) => {
  switch (handle) {
    case ProductHandle.SuperPowerBrief:
      return 'Recommended-SPB.jpg';
    case ProductHandle.QueenBrief:
      return 'Recommended-QB.jpg';
    case ProductHandle.LiftWearTank:
      return 'Recommended-LWT.jpg';
    case ProductHandle.LiftWearCami:
      return 'Recommended-LWC.jpg';
    case ProductHandle.SuperPowerThong:
      return 'Recommended-SPT.jpg';
    case ProductHandle.VNeckBra:
      return 'Recommended-VNB.jpg';
    case ProductHandle.SilhouetteBra:
      return 'Recommended-LWSB.jpg';
    case ProductHandle.SilhouetteBrief:
      return 'Recommended-LWSB.jpg';
    case ProductHandle.CrossoverBra:
      return 'Recommended-LWBR03.jpg';
    case ProductHandle.EverydayCottonBrief:
      return 'Recommended-ECB.jpg';
    case ProductHandle.EverydayCottonThong:
      return 'Recommended-ECT.jpg';
    case ProductHandle.CamiBodysuit:
      return 'Recommended-LWCB.jpg';
    case ProductHandle.TankBodysuit:
      return 'Recommended-LWTB.jpg';
    case ProductHandle.BoldnessBodysuit:
      return 'Recommended-BB.jpg';
    case ProductHandle.WaistHeroCincher:
      return 'Recommended-WHC.jpg';
    case ProductHandle.EverReadyPant:
      return 'Recommended-ERP.jpg';
    case ProductHandle.LeggingTwo:
      return 'Recommended-LT.jpg';
    case ProductHandle.SuperPowerShort:
    default:
      return 'Recommended-SPS.jpg';
  }
};

export const getPopularity = (handle: ProductHandle): number => {
  switch (handle) {
    case ProductHandle.SuperPowerShort:
      return 1;
    case ProductHandle.LiftWearCami:
      return 2;
    case ProductHandle.CrossoverBra:
      return 3;
    case ProductHandle.LeggingTwo:
      return 4;
    case ProductHandle.EverReadyPant:
      return 5;
    case ProductHandle.CamiBodysuit:
      return 6;
    case ProductHandle.SuperPowerBrief:
      return 9;
    case ProductHandle.SuperPowerThong:
      return 10;
    case ProductHandle.QueenBrief:
      return 11;
    case ProductHandle.LiftWearTank:
      return 12;
    case ProductHandle.SilhouetteBra:
      return 13;
    case ProductHandle.SilhouetteBrief:
      return 14;
    case ProductHandle.VNeckBra:
      return 15;
    case ProductHandle.WaistHeroCincher:
      return 16;
    case ProductHandle.EverydayCottonBrief:
      return 17;
    case ProductHandle.EverydayCottonThong:
      return 18;
    case ProductHandle.TankBodysuit:
      return 19;
    case ProductHandle.BoldnessTank:
      return 20;
    case ProductHandle.BoldnessBodysuit:
      return 21;
    default:
      return 22;
  }
};

export const getDiscounted = (handle: ProductHandle): number => {
  switch (handle) {
    case ProductHandle.SuperPowerShort:
      return 1;
    case ProductHandle.LiftWearTank:
      return 2;
    case ProductHandle.VNeckBra:
      return 3;
    case ProductHandle.SculptwaveCapri:
      return 4;
    case ProductHandle.SuperPowerBrief:
      return 5;
    case ProductHandle.SilhouetteBra:
      return 6;
    case ProductHandle.EverydayCottonBrief:
      return 7;
    case ProductHandle.EverydayCottonThong:
      return 8;
    case ProductHandle.LiftWearCami:
      return 9;
    case ProductHandle.CrossoverBra:
      return 10;
    case ProductHandle.LeggingTwo:
      return 11;
    case ProductHandle.EverReadyPant:
      return 12;
    case ProductHandle.CamiBodysuit:
      return 13;
    case ProductHandle.BoldnessBodysuit:
    case ProductHandle.BoldnessTank:
    case ProductHandle.TankBodysuit:
      return 14;
    case ProductHandle.WaistHeroCincher:
      return 14;
    case ProductHandle.SuperPowerThong:
      return 15;
    case ProductHandle.QueenBrief:
      return 16;
    default:
      return 17;
  }
};

const filterAndSortRelatedProducts = (products: ViewProduct[], handle: ProductHandle): ViewProduct[] => {
  const findProduct = (h: ProductHandle) => products.find((p) => p.handle === h);
  const activeProduct = findProduct(handle);
  return products
    .filter((p) => !p.hiddenInMenus) // take this filtering+sorting, and apply it to addOnProducts
    .filter((p) => p.handle !== handle)
    .filter((p) => ![ProductCategory.AddOns, ProductCategory.Miscellaneous, ProductCategory.GiftCard].includes(p.type))
    .sort((a, b) => {
      const popularitySorter = getPopularity(a.handle) > getPopularity(b.handle) ? 1 : -1;
      // sort same category as product by popularity
      if (a.type === b.type && a.type === activeProduct?.type) return popularitySorter;
      // sort same category above other categories
      if (a.type === activeProduct?.type) return -1;
      if (b.type === activeProduct?.type) return 1;
      // sort popular products next
      return popularitySorter;
    });
};

export const dynamicRelatedItems = (handle: ProductHandle, products: ViewProduct[]): RelatedItem[] => {
  const addOnProducts = filterAndSortRelatedProducts(
    products.filter((p) => p.addOn),
    handle,
  );
  const otherProducts = filterAndSortRelatedProducts(products, handle);

  // add upsell items if available
  const relatedProducts = addOnProducts[0] ? [...otherProducts.slice(0, 3), addOnProducts[0]] : otherProducts.slice(0, 4);
  return relatedProducts.map((p) => items(p));
};

export const mostPopularItems = (products: ViewProduct[]): RelatedItem[] => {
  const allPopularProducts = products
    .filter((p) => !p.hiddenInMenus) // take this filtering+sorting, and apply it to addOnProducts
    .filter((p) => ![ProductCategory.AddOns, ProductCategory.Miscellaneous, ProductCategory.GiftCard].includes(p.type))
    .sort((a, b) => {
      return getPopularity(a.handle) > getPopularity(b.handle) ? 1 : -1;
    });

  // add upsell items if available
  const relatedProducts = allPopularProducts.slice(0, 4);
  return relatedProducts.map((p) => items(p));
};

type AssetPathFn = (file: string) => string;
type SelectStyle = (handle: ProductHandle, subContext: SubContext) => void;
interface RelatedItem {
  name1: string;
  name2: string;
  image: string;
  handle: ProductHandle;
  subtitle?: string;
}

const renderRelatedItemMobile =
  (assetPathFn: AssetPathFn, selectStyleAndScroll: SelectStyle) =>
  ({ name1, name2, subtitle, image, handle }: RelatedItem, index: number) =>
    (
      <div key={index} className={styles.relatedProductWrapper}>
        <div className={styles.relatedProduct}>
          <div className={styles.relatedImageWrapper}>
            <button onClick={() => selectStyleAndScroll(handle, SubContext.RelatedItem)}>
              <LazyImageLoader alt="related" className={styles.relatedImage} src={imageUrl(assetPathFn, image)} />
            </button>
          </div>
          <h2>
            {name1} <span>{name2}</span>
          </h2>
          <div className={styles.relatedProductSubTitle}>{subtitle}</div>
        </div>
      </div>
    );

const renderRelatedItemDesktop =
  (assetPathFn: AssetPathFn, selectStyleAndScroll: SelectStyle) =>
  ({ name1, name2, subtitle, image, handle }: RelatedItem, index: number) =>
    (
      <div className={styles.relatedItem} key={index}>
        <div className={styles.relatedImageWrapper}>
          <button onClick={() => selectStyleAndScroll(handle, SubContext.RelatedItem)}>
            <LazyImageLoader alt="related" className={styles.relatedImage} src={imageUrl(assetPathFn, image)} />
          </button>
        </div>
        <button onClick={() => selectStyleAndScroll(handle, SubContext.RelatedItem)} className={styles.cta}>
          Learn more
        </button>
        <h2 itemProp="name">
          {name1} <span>{name2}</span>
        </h2>
        <div className={styles.relatedItemSubtitle}>{subtitle}</div>
      </div>
    );

const imageUrl = (assetPathFn: AssetPathFn, image: string) => assetPathFn(image.replace('.jpg', '_300x.jpg'));

export default ProductRelatedItems;
